import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {Alert, Badge, Button, Form, Table, Pagination} from 'react-bootstrap'
import {KTCard, KTCardBody} from '../../../components'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {
  digitalPlanetListSessions,
  clearError,
  selectDigitalPlanetSessionList,
  selectDigitalPlanetLoading,
  selectDigitalPlanetError,
} from '../../../redux/slices/digitalplanetinvoice.slice'
import {ImportResultModal} from '../DigitalPlanetInvoices/ImportResultModal'

const PAGE_SIZE = 10

function formatDateTime(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const iso = typeof dateStr === 'string' ? dateStr : dateStr.toISOString()
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}.${m}.${y}`
}

function List() {
  const dispatch = useDispatch()
  const sessionList = useSelector(selectDigitalPlanetSessionList)
  const loading = useSelector(selectDigitalPlanetLoading)
  const error = useSelector(selectDigitalPlanetError)

  const [selectedSession, setSelectedSession] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortConfig, setSortConfig] = useState({key: 'importedAt', dir: 'desc'})

  const handleRefresh = useCallback(() => {
    dispatch(clearError())
    dispatch(digitalPlanetListSessions())
  }, [dispatch])

  useEffect(() => {
    dispatch(digitalPlanetListSessions())
  }, [dispatch])

  const handleSort = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key ? {key, dir: prev.dir === 'asc' ? 'desc' : 'asc'} : {key, dir: 'desc'}
    )
  }, [])

  const filteredSessions = useMemo(() => {
    if (!sessionList) return []
    let list = sessionList
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      list = list.filter((s) => (s.importedByUserName || '').toLowerCase().includes(q))
    }
    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        const aVal = a[sortConfig.key] ?? ''
        const bVal = b[sortConfig.key] ?? ''
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        return sortConfig.dir === 'asc' ? cmp : -cmp
      })
    }
    return list
  }, [sessionList, searchText, sortConfig])

  useEffect(() => {
    setCurrentPage(1)
  }, [filteredSessions])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredSessions.length / PAGE_SIZE)),
    [filteredSessions]
  )

  const pagedSessions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredSessions.slice(start, start + PAGE_SIZE)
  }, [filteredSessions, currentPage])

  const stats = useMemo(() => {
    if (!sessionList) return null
    return {
      total: sessionList.length,
      totalSuccess: sessionList.reduce((sum, s) => sum + (s.successCount || 0), 0),
      totalFailure: sessionList.reduce((sum, s) => sum + (s.failureCount || 0), 0),
    }
  }, [sessionList])

  const handleOpenDetail = useCallback((session) => {
    setSelectedSession({
      importSessionId: session.id,
      successCount: session.successCount,
      failureCount: session.failureCount,
    })
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedSession(null)
  }, [])

  const SortIcon = ({column}) => {
    if (sortConfig.key !== column)
      return <span className='text-muted ms-1' style={{fontSize: 11}}>↕</span>
    return (
      <span className='ms-1' style={{fontSize: 11}}>
        {sortConfig.dir === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const sortableHeader = (label, key, className = '') => (
    <th
      className={className}
      style={{cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap'}}
      onClick={() => handleSort(key)}
    >
      {label}
      <SortIcon column={key} />
    </th>
  )

  return (
    <>
      <PageTitle>Aktarım Sonuçları</PageTitle>
      <KTCard>
        <KTCardBody>
          {error && (
            <Alert variant='danger' onClose={() => dispatch(clearError())} dismissible>
              {error}
            </Alert>
          )}

          {loading && <ListLoading />}

          {!loading && !error && !sessionList && (
            <div className='text-center text-muted py-10'>
              Veriler yüklenemedi.{' '}
              <Button variant='link' className='p-0' onClick={handleRefresh}>
                Tekrar dene
              </Button>
            </div>
          )}

          {!loading && sessionList && (
            <>
              {/* Stats bar */}
              {stats && stats.total > 0 && (
                <div className='d-flex gap-3 mb-5 flex-wrap'>
                  <div className='border border-dashed rounded px-4 py-2 text-center min-w-80px'>
                    <div className='fw-bold fs-4'>{stats.total}</div>
                    <div className='text-muted fs-7'>Aktarım</div>
                  </div>
                  <div className='border border-dashed rounded px-4 py-2 text-center min-w-80px'>
                    <div className='fw-bold fs-4 text-success'>{stats.totalSuccess}</div>
                    <div className='text-muted fs-7'>Başarılı Fatura</div>
                  </div>
                  {stats.totalFailure > 0 && (
                    <div className='border border-dashed rounded px-4 py-2 text-center min-w-80px'>
                      <div className='fw-bold fs-4 text-danger'>{stats.totalFailure}</div>
                      <div className='text-muted fs-7'>Hatalı Fatura</div>
                    </div>
                  )}
                </div>
              )}

              {/* Filter bar */}
              <div className='d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap'>
                <Form.Control
                  type='text'
                  placeholder='Aktaran kullanıcıya göre ara...'
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{maxWidth: 280}}
                />
                <Button variant='light' size='sm' onClick={handleRefresh} disabled={loading}>
                  <i className='bi bi-arrow-clockwise me-1' />
                  Yenile
                </Button>
              </div>

              <div className='table-responsive'>
                <Table hover className='table align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted bg-light'>
                      {sortableHeader('Aktarım Tarihi', 'importedAt', 'ps-4 rounded-start')}
                      <th>Dönem</th>
                      {sortableHeader('Aktaran', 'importedByUserName')}
                      {sortableHeader('Başarılı', 'successCount', 'text-center')}
                      {sortableHeader('Hatalı', 'failureCount', 'text-center')}
                      <th className='rounded-end text-center'>Detay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.length === 0 && (
                      <tr>
                        <td colSpan={6} className='text-center text-muted py-6'>
                          {searchText
                            ? 'Aramayla eşleşen kayıt bulunamadı.'
                            : 'Henüz aktarım yapılmamış.'}
                        </td>
                      </tr>
                    )}
                    {pagedSessions.map((session) => (
                      <tr
                        key={session.id}
                        style={{cursor: 'pointer'}}
                        onClick={() => handleOpenDetail(session)}
                      >
                        <td className='ps-4'>{formatDateTime(session.importedAt)}</td>
                        <td>
                          {formatDate(session.startDate)} – {formatDate(session.endDate)}
                        </td>
                        <td>{session.importedByUserName || '-'}</td>
                        <td className='text-center'>
                          <Badge bg='success'>{session.successCount}</Badge>
                        </td>
                        <td className='text-center'>
                          {session.failureCount > 0 ? (
                            <Badge bg='danger'>{session.failureCount}</Badge>
                          ) : (
                            <Badge bg='secondary'>{session.failureCount}</Badge>
                          )}
                        </td>
                        <td className='text-center'>
                          <Button
                            variant='light-primary'
                            size='sm'
                            title='Detayı görüntüle'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenDetail(session)
                            }}
                          >
                            <i className='bi bi-list-ul' />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className='d-flex justify-content-between align-items-center mt-4'>
                  <span className='text-muted fs-7'>
                    {filteredSessions.length === 0
                      ? '0 kayıt'
                      : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                          currentPage * PAGE_SIZE,
                          filteredSessions.length
                        )} / ${filteredSessions.length} kayıt`}
                  </span>
                  <Pagination className='mb-0'>
                    <Pagination.First
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    />
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    />
                    {Array.from({length: totalPages}, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p)
                        acc.push(p)
                        return acc
                      }, [])
                      .map((item) =>
                        typeof item === 'string' ? (
                          <Pagination.Ellipsis key={item} disabled />
                        ) : (
                          <Pagination.Item
                            key={item}
                            active={item === currentPage}
                            onClick={() => setCurrentPage(item)}
                          >
                            {item}
                          </Pagination.Item>
                        )
                      )}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    />
                    <Pagination.Last
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </KTCardBody>
      </KTCard>

      {selectedSession && (
        <ImportResultModal
          show={!!selectedSession}
          result={selectedSession}
          onClose={handleCloseDetail}
          modalTitle='Aktarım Detayı'
        />
      )}
    </>
  )
}

export {List}
